import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import SaveIcon from '@mui/icons-material/Save';
import styled from 'styled-components';
import { OfficialsSelectInput } from '../../components/OfficialsSelectInput';
import { useFeathers } from '../../app/util';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { Official } from '../../app/feathers/officials/Official';
import { CirclePhoto } from '../../components';
import { District } from '../../app/feathers/districts/District';

type OfficialWithRecentCandidateDistrict = Official & {
  recentCandidate?: Official['recentCandidate'] & {
    district?: string;
    stagingDraft?: {
      district?: string;
    };
  };
};

type OfficialSelectOption = Official & {
  isNew?: boolean;
};

type OfficialSelectChangeEvent = { target: { value: OfficialSelectOption } };

interface OfficialsCellProps {
  candidate: { name?: string };
  officialInfo: Official | null;
  officialMatch: {
    official?: string;
    create: boolean;
  } | null;
  loading: boolean;
  onOfficialChange?: (officialMatch: { official?: Official; create: boolean } | null) => void;
  isDuplicate?: boolean;
}

const OfficialsCell: React.FC<OfficialsCellProps> = ({ 
  candidate, 
  officialMatch,
  officialInfo, 
  loading, 
  onOfficialChange,
  isDuplicate = false
}) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [isEditing, setIsEditing] = useState(false);
  const [district, setDistrict] = useState<District | null>(null);

  // Load the district for this official's most recent candidate so we can
  // display the state (from the district longName) in the subtext.
  useEffect(() => {
    const officialWithDistrict = officialInfo as OfficialWithRecentCandidateDistrict | null;
    const districtId = officialWithDistrict?.recentCandidate?.stagingDraft?.district
      ?? officialWithDistrict?.recentCandidate?.district;

    if (!districtId) {
      setDistrict(null);
      return;
    }

    let isMounted = true;

    feathers
      .getService('districts')
      .get(districtId)
      .then((loadedDistrict: District) => {
        if (isMounted) {
          setDistrict(loadedDistrict);
        }
      })
      .catch(() => {
        if (isMounted) {
          setDistrict(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [feathers, officialInfo]);

  const handleOfficialChange = (e: OfficialSelectChangeEvent) => {
    const { value } = e.target;

    if (onOfficialChange) {
      let newOfficialMatch: { official?: Official; create: boolean } | null = null;
      
      if (value.isNew) {
        newOfficialMatch = { create: true };
      } else {
        newOfficialMatch = { official: value as Official, create: false };
      }
      
      onOfficialChange(newOfficialMatch);
    }
    
    setIsEditing(false);
  };

  if (loading) {
    return (
      <Skeleton variant="text" width={100} height={24} />
    );
  }

  if (isEditing) {
    return (
      <Box sx={{ 
        minWidth: 200, 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: 1,
        position: 'relative'
      }}>
        <OfficialsSelectInput
          value={null}
          seedSearchText={candidate.name || ''}
          onChange={handleOfficialChange}
          style={{ width: '100%' }}
          className=""
          office={null}
          district={null}
        />
      </Box>
    );
  }

  // Handle click to enter editing mode
  const handleClick = () => {
    setIsEditing(true);
  };

  if (officialMatch?.create) {
    return (
      <PurplePill onClick={handleClick} style={{ cursor: 'pointer' }}>
        <Typography variant="body2" sx={{ color: '#6a1b9a', fontSize: '12px' }}>
          Create as new
        </Typography>
      </PurplePill>
    );
  }

  if (officialMatch?.official) {
    const PillComponent = isDuplicate ? DuplicatePill : PurplePill;
    const partyLetter = officialInfo?.recentCandidate?.party;
    const districtName = district?.longName;
    const hasSubtext = Boolean(partyLetter || districtName);

    return (
      <PillComponent
        onClick={handleClick}
        style={{
          cursor: 'pointer',
          padding: officialInfo ? '0px 8px 0 4px' : '4px 12px',
        }}
      >
        {officialInfo && (
          <CirclePhoto
            photoPath={officialInfo?.recentCandidate?.photoPathFace}
            hasVariants={officialInfo?.recentCandidate?.hasVariants === true}
            highlight={false}
            checkmark={false}
            style={{ marginRight: 4, marginLeft: '-4px' }}
            size="mini"
          />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <Typography
            variant="body2"
            sx={{ color: isDuplicate ? '#856404' : '#6a1b9a', fontSize: '12px' }}
          >
            {officialInfo?.name || officialMatch.official}
          </Typography>
          {hasSubtext && (
            <Typography
              variant="caption"
              sx={{
                color: isDuplicate ? '#856404' : '#6a1b9a',
                fontSize: '10px',
                opacity: 0.8,
              }}
            >
              {districtName}
              {partyLetter && districtName ? ' • ' : ''}
              {partyLetter ? `Party: ${partyLetter}` : ''}
            </Typography>
          )}
        </Box>
        {isDuplicate && (
          <Typography
            variant="body2"
            sx={{
              color: '#856404',
              fontSize: '10px',
              marginLeft: '4px',
              fontWeight: '600',
            }}
          >
            (Duplicate)
          </Typography>
        )}
      </PillComponent>
    );
  }

  // No officialMatch provided - needs attention
  return (
    <NeedsAttentionPill onClick={handleClick} style={{ cursor: 'pointer' }}>
      <Typography variant="body2" sx={{ color: '#d32f2f', fontSize: '12px' }}>
        Needs attention
      </Typography>
    </NeedsAttentionPill>
  );
};



const PurplePill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border: 1px solid #6a1b9a;
  border-radius: 16px;
  background-color: #fff;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f3e5f5;
    border-color: #4a148c;
  }
`;

const NeedsAttentionPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border: 1px solid #d32f2f;
  border-radius: 16px;
  background-color: #ffebee;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #ffcdd2;
    border-color: #b71c1c;
  }
`;

const DuplicatePill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border: 1px solid #ffc107;
  border-radius: 16px;
  background-color: #fff3cd;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #ffe69c;
    border-color: #ff9800;
  }
`;

export default OfficialsCell; 